import React, { useState, useEffect, useMemo, useRef, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Dropdown,
  Form,
  notification,
  Tag,
  Pagination,
  Row,
  Col,
  Avatar,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  MoreOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  StarOutlined,
} from '@ant-design/icons';
import {
  CreateMemberRequest,
  UpdateMemberRequest,
  UserScopes,
} from '@kemu-io/kemu-types';
import organizationApi from '../../../api/organization/organizationApi';
import useTranslation from '@hooks/useTranslation';
import styles from './UserManagement.module.css';
import StyledButton from '@components/form-control/styledButton/styledButton';
import { NewMemberDetails, useAccountMenuContext } from '../AccountMenuContext';
import { openCheckoutModalAction } from '@src/app/reducers/checkout/checkoutSlice';
import useConfirmModal from '@src/common/hooks/useConfirmModal';

const { Option } = Select;

// Based on the actual API structure from linting errors
type Member = NewMemberDetails;

type ResponseError = {
  message: string;
  code: number;
  errorCode: string;
};

const getResponseError = (error: any): ResponseError => {
  if (error.response?.data?.message) {
    return {
      message: error.response?.data?.message,
      code: error.response?.data?.code,
      errorCode: error.response?.data?.errorCode,
    };
  }
  return {
    message: error.message,
    code: error.status,
    errorCode: error.code,
  };
};

const getRoleColor = (role: string) => {
  if (role === 'Owner') {
    return 'blue';
  } else if (role === 'Admin') {
    return 'orange';
  }

  return 'green';
};

const UserManagement: React.FC = () => {
  const dispatch = useDispatch();
  const { 
    members, loadingMembers, 
    handleFetchMembers, setEditingMemberId, editingMemberId, 
    getTempMemberDetails, setTempMemberDetails 
  } = useAccountMenuContext();
  const t = useTranslation('AccountMenu.Modal.UserManagement');
  const tCommon = t.withBaseKey('CommonWords');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editingMember, setEditingMember] = useState<NewMemberDetails | null>(() => getTempMemberDetails());
  const [showForm, setShowForm] = useState(editingMemberId !== null);
  const [showConfirm, contextHolder] = useConfirmModal();
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();
  const remainingSeats = members?.remainingSeats || 0;
  const hasUnlimitedRemainingSeats = remainingSeats < 0;
  const noSeatsRemaining = remainingSeats === 0;

  /**
   * Handles the upgrade action by opening the checkout modal
   */
  const handleUpgrade = useCallback(() => {
    dispatch(openCheckoutModalAction());
  }, [dispatch]);

  const notifyError = (translationKey: string, error: any) => {
    const responseError = getResponseError(error);
    notification.error({
      message: t(translationKey, { reason: responseError.message }),
    });
  };


  /**
   * Creates a new organization member
   */
  const createMember = async (memberData: CreateMemberRequest) => {
    setFormLoading(true);
    try {
      await organizationApi.addOrganizationMember(memberData);
      notification.success({
        message: t('Actions.Success.UserCreated'),
      });
      handleFetchMembers();
      setShowForm(false);
      setEditingMember(null);
      form.resetFields();
    } catch (error) {
      console.error('Failed to create member:', error);
      notifyError('Actions.Error.UserCreated', error);
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Updates an existing organization member
   */
  const updateMember = async (
    memberId: string,
    memberData: UpdateMemberRequest
  ) => {
    setFormLoading(true);
    try {
      await organizationApi.updateOrganizationMember(memberId, memberData);
      notification.success({
        message: t('Actions.Success.UserUpdated'),
      });
      handleFetchMembers();
      setShowForm(false);
      setEditingMember(null);
      form.resetFields();
    } catch (error) {
      console.error('Failed to update member:', error);
      notifyError('Actions.Error.UserUpdated', error);
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * Handles member removal from organization
   */
  const handleRemoveMember = (member: Member) => {
    // Prevent removing the owner
    if (member.role === 'Owner') {
      notification.warning({
        message: t('Actions.Warning.CannotRemoveOwner'),
        description: t('Actions.Warning.OwnerCannotBeRemoved'),
      });
      return;
    }

    showConfirm({
      type: 'danger',
      title: `${tCommon('Remove')} ${tCommon('User')}`,
      content: t('RemoveConfirm.Content', {
        name:
          `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
          member.email ||
          member.loginId ||
          'Unknown',
      }),
      onOk: async () => {
        try {
          await organizationApi.deleteMember(member.id as string);
          notification.success({
            message: t('Actions.Success.UserRemoved'),
          });
          handleFetchMembers();
        } catch (error) {
          console.error('Failed to remove member:', error);
          notifyError('Actions.Error.UserRemoved', error);
        }
      },
    });
  };

  /**
   * Handles member status change (disable/enable)
   */
  const handleToggleStatus = (member: Member) => {
    // Prevent disabling the owner
    if (member.role === 'Owner') {
      notification.warning({
        message: t('Actions.Warning.CannotDisableOwner'),
        description: t('Actions.Warning.OwnerCannotBeDisabled'),
      });
      return;
    }

    const isDisabling = member.status === 'active';
    const action = isDisabling ? tCommon('Disable') : tCommon('Enable');
    const contentKey = isDisabling
      ? 'DisableConfirm.Content'
      : 'EnableConfirm.Content';

    showConfirm({
      title: `${action} ${tCommon('User')}`,
      content: t(contentKey, {
        name:
          `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
          member.email ||
          member.loginId ||
          'Unknown',
      }),
      onOk: async () => {
        try {
          await updateMember(member.id as string, {
            member: {
              status: isDisabling ? 'inactive' : 'active',
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
              role: member.role,
              groups: member.groups,
            },
          });
          const successKey = isDisabling
            ? 'Actions.Success.UserDisabled'
            : 'Actions.Success.UserEnabled';
          notification.success({
            message: t(successKey),
          });
        } catch (error) {
          const errorKey = isDisabling
            ? 'Actions.Error.UserDisabled'
            : 'Actions.Error.UserEnabled';
          notification.error({
            message: t(errorKey),
          });
        }
      },
    });
  };


  /**
   * Opens the edit form for a member
   */
  const handleEditMember = (member: Member) => {
    setEditingMember(member);
    setEditingMemberId(member.id as string);
    setTempMemberDetails(member);
    setShowForm(true);
    form.setFieldsValue({
      email: member.email,
      firstName: member.firstName,
      lastName: member.lastName,
      role: member.role,
      groups: member.groups,
      status: member.status,
      permissions: member.entityScopes?.organization?.scopes || [],
    });
  };

  /**
   * Handles going back from the form to the list
   */
  const handleBackToList = () => {
    setShowForm(false);
    setTempMemberDetails(null);
    setEditingMember(null);
    setEditingMemberId(null);
    form.resetFields();
  };

  /**
   * Handles form submission for creating or updating a member
   */
  const handleFormSubmit = async (values: any) => {
    const permissions = values.permissions as UserScopes[] | undefined;
    const entityScopes = permissions && permissions.length > 0
      ? { organization: { scopes: permissions } }
      : undefined;

    if (editingMember) {
      await updateMember(editingMember.id as string, {
        member: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          role: values.role,
          groups: values.groups,
          status: values.status,
          entityScopes,
        },
      });
    } else {
      await createMember({
        ...values,
        entityScopes,
      });
    }
  };

  /**
   * Renders status tag with appropriate color
   */
  const renderStatusTag = (status: Member['status']) => {
    const colors = {
      active: 'green',
      inactive: 'orange',
    };

    return (
      <Tag color={colors[status]}>
        {status === 'active' ? tCommon('Active') : tCommon('Inactive')}
      </Tag>
    );
  };

  /**
   * Formats the last login date
   */
  const formatLastLogin = (lastSeenAt?: string) => {
    if (!lastSeenAt) { return 'Never'; }

    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    );

    if (diffInHours < 1) { return 'Just now'; }
    if (diffInHours < 24) { return `${diffInHours} hours ago`; }
    if (diffInHours < 168) { return `${Math.floor(diffInHours / 24)} days ago`; }

    return date.toLocaleDateString();
  };

  /**
   * Creates dropdown menu for member actions
   */
  const getActionMenu = (member: Member): MenuProps => {
    const isOwner = member.role === 'Owner';
    
    return {
      items: [
        {
          key: 'edit',
          title: '',
          icon: <EditOutlined />,
          label: t('Actions.EditDetails'),
          onClick: () => handleEditMember(member),
        },
        {
          key: 'toggle-status',
          title: '',
          icon:
            member.status === 'active' ? (
              <StopOutlined />
            ) : (
              <PlayCircleOutlined />
            ),
          label:
            member.status === 'active' ? tCommon('Disable') : tCommon('Enable'),
          onClick: () => handleToggleStatus(member),
          disabled: isOwner,
        },
        // {
        //   key: 'reset-password',
        //   icon: <LockOutlined />,
        //   label: t('Actions.ResetPassword'),
        //   onClick: () => handleResetPassword(member),
        // },
        {
          type: 'divider',
        },
        {
          key: 'remove',
          title: '',
          icon: <DeleteOutlined />,
          label: t('Actions.RemoveFromOrganization'),
          danger: true,
          onClick: () => handleRemoveMember(member),
          disabled: isOwner || member.status === 'active',
        },
      ],
    };
  };

  /**
   * Filters members based on search text, status, and group
   */
  const filteredMembers = useMemo(() => {
    return members?.members.filter((member) => {
      const matchesSearch =
        !searchText ||
        member.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        `${member.firstName} ${member.lastName}`
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        member.id.toLowerCase().includes(searchText.toLowerCase()) ||
        member.loginId?.toLowerCase().includes(searchText.toLowerCase());

        const matchesStatus =
          statusFilter === 'all' || member.status === statusFilter;

        const matchesGroup =
          groupFilter === 'all' ||
          (member.groups && member.groups.includes(groupFilter));

      return matchesSearch && matchesStatus && matchesGroup;
    });
  }, [members, searchText, statusFilter, groupFilter]);

  /**
   * Gets paginated members for current page
   */
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredMembers?.slice(startIndex, startIndex + pageSize);
  }, [filteredMembers, currentPage, pageSize]);

  /**
   * Gets unique groups from all members
   */
  const availableGroups = useMemo(() => {
    const groups = new Set<string>();
    members?.members.forEach((member) => {
      member.groups?.forEach((group) => groups.add(group));
    });
    return Array.from(groups);
  }, [members]);

  /**
   * Table columns configuration
   */
  const columns: ColumnsType<Member> = [
    {
      title: tCommon('Email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string | undefined, record: Member) => (
        <Space>
          <Avatar size='small' icon={<UserOutlined />} />
          <span>{email || record.loginId || '-'}</span>
        </Space>
      ),
    },
    {
      title: tCommon('Name'),
      key: 'name',
      render: (_, record: Member) =>
        `${record.firstName} ${record.lastName}`.trim() || '-',
    },
    {
      title: tCommon('Roles'),
      dataIndex: 'role',
      key: 'role',
      render: (role: Member['role']) => (
        <Tag color={getRoleColor(role)}>{role.charAt(0).toUpperCase() + role.slice(1)}</Tag>
      ),
    },
    {
      title: tCommon('Groups'),
      dataIndex: 'groups',
      key: 'groups',
      render: (groups: string[]) => (
        <Space size={[0, 8]} wrap>
          {groups?.map((group) => (
            <Tag key={group} color='default'>
              {group}
            </Tag>
          )) || '-'}
        </Space>
      ),
    },
    {
      title: tCommon('Status'),
      dataIndex: 'status',
      key: 'status',
      render: renderStatusTag,
    },
    {
      title: t('Column.LastLogin'),
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: formatLastLogin,
    },
    {
      title: tCommon('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record: Member) => (
        <Dropdown menu={getActionMenu(record)} trigger={['click']}>
          <Button type='text' icon={<MoreOutlined />} title="" />
        </Dropdown>
      ),
    },
  ];

  /**
   * Renders the user form with slide animation
   */
  const renderUserForm = () => (
    <div className={`${styles.FormContainer} ${showForm ? styles.slideIn : ''}`}>
      <div className={styles.FormHeader}>
        <Button 
          className={styles.BackButton}
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBackToList}
        />
        <div className={styles.FormTitle}>
          {editingMember ? t('Form.Title.Edit') : t('Form.Title.Create')}
        </div>
      </div>

      <Spin spinning={formLoading} indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />}>
        <Form
          form={form}
          layout='vertical'
          onFinish={handleFormSubmit}
          requiredMark={false}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='email'
                label={tCommon('Email')}
                rules={[
                  { required: true, message: t('Form.EmailRequired') },
                  {
                    type: 'email',
                    message: 'Please enter a valid email address',
                  },
                ]}
              >
                <Input
                  placeholder={t('Form.EmailPlaceholder')}
                  disabled={!!editingMember}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='firstName'
                label={tCommon('FirstName')}
                rules={[
                  { required: true, message: t('Form.FirstNameRequired') },
                ]}
              >
                <Input placeholder={t('Form.FirstNamePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='lastName' label={tCommon('LastName')}>
                <Input placeholder={t('Form.LastNamePlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='role'
                label={tCommon('Role')}
                rules={[{ required: true, message: t('Form.RoleRequired') }]}
              >
                <Select 
                  placeholder={t('Form.RolePlaceholder')}
                  disabled={editingMember?.role === 'Owner'}
                >
                  {editingMember?.role === 'Owner' && (
                    <Option value='Owner'>{tCommon('Owner')}</Option>
                  )}
                  <Option value='Admin'>{tCommon('Admin')}</Option>
                  <Option value='Member'>{tCommon('Member')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='status' label={tCommon('Status')}>
                <Select 
                  placeholder={t('Form.StatusPlaceholder')}
                  disabled={editingMember?.role === 'Owner'}
                >
                  <Option value='active'>{tCommon('Active')}</Option>
                  <Option value='inactive'>{tCommon('Inactive')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name='groups' label={tCommon('Groups')}>
            <Select
              mode='tags'
              placeholder={t('Form.GroupsPlaceholder')}
              options={availableGroups.map((group) => ({
                label: group,
                value: group,
              }))}
            />
          </Form.Item>

          <Form.Item name='permissions' label={tCommon('Permissions')}>
            <Select
              mode='multiple'
              placeholder={t('Form.PermissionsPlaceholder')}
            >
              <Option value='CreateApiKeys'>{t('Form.Permission.CreateApiKeys')}</Option>
              <Option value='RevokeApiKeys'>{t('Form.Permission.RevokeApiKeys')}</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <StyledButton noShadow color='light' title={tCommon('Cancel')} onClick={handleBackToList} />
              <StyledButton noShadow color='primary' title={editingMember ? tCommon('Update') : tCommon('Create')} type='submit' loading={formLoading} />
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );

  useEffect(() => {
    if (editingMemberId) {
      const matchingMember = members?.members.find((member) => member.id === editingMemberId) || null;
      if (matchingMember) {
        handleEditMember(matchingMember);
      }
    }
  }, [editingMemberId, members]);

  // useEffect(() => {
  //   if (!loadingMembersRef.current) {
  //     loadingMembersRef.current = true;
  //     onFetchMembers();
  //   }
  // }, []);

  return (
    <Spin spinning={loadingMembers} indicator={<LoadingOutlined spin style={{ fontSize: 48 }} />}> 
      <div className={styles.Wrapper}>
        {contextHolder}
        <div className={styles.SectionTitle}>{t('Title')}</div>

        <div className={styles.ContentContainer}>
          {/* List Container with slide animation */}
          <div className={`${styles.ListContainer} ${showForm ? styles.slideOut : ''}`}>
            <Space direction='vertical' size='large' style={{ width: '100%' }}>
              <Row justify='end' style={{ marginBottom: 12 }}>
                <Col>
                  <div className={styles.CreateUserContainer}>
                    {!hasUnlimitedRemainingSeats && (
                      <div className={`${styles.RemainingSeatsBadge} ${noSeatsRemaining ? styles.warning : ''}`}>
                        <UserOutlined className={`${styles.RemainingSeatsIcon} ${noSeatsRemaining ? styles.warning : ''}`} />
                        <span>{t(remainingSeats === 1 ? 'RemainingSeatOne' : 'RemainingSeatMany', { count: remainingSeats })}</span>
                      </div>
                    )}
                    {noSeatsRemaining ? (
                      <button
                        className={styles.UpgradeButton}
                        onClick={handleUpgrade}
                      >
                        <StarOutlined className={styles.UpgradeIcon} />
                        {t('Actions.Upgrade')}
                      </button>
                    ) : (
                      <StyledButton
                        color='primary'
                        icon={<PlusOutlined />}
                        noShadow
                        title={t('Actions.CreateUser')}
                        onClick={() => {
                          setEditingMember(null);
                          setShowForm(true);
                          form.setFieldsValue({
                            email: '',
                            firstName: '',
                            lastName: '',
                            role: 'Member',
                            status: 'active',
                            groups: [],
                            permissions: [],
                          });
                        }}
                      />
                    )}
                  </div>
                </Col>
              </Row>
              {/* Search and Filters */}
              <Row gutter={16}>
                <Col flex='auto'>
                  <Input
                    placeholder={t('SearchPlaceholder')}
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col>
                  <Select
                    style={{ width: 150 }}
                    placeholder={tCommon('Status')}
                    value={statusFilter}
                    onChange={setStatusFilter}
                  >
                    <Option value='all'>{t('Filters.AllStatuses')}</Option>
                    <Option value='active'>{tCommon('Active')}</Option>
                    <Option value='inactive'>{tCommon('Inactive')}</Option>
                  </Select>
                </Col>
                <Col>
                  <Select
                    style={{ width: 150 }}
                    placeholder={tCommon('Group')}
                    value={groupFilter}
                    onChange={setGroupFilter}
                  >
                    <Option value='all'>{t('Filters.AllGroups')}</Option>
                    {availableGroups.map((group) => (
                      <Option key={group} value={group}>
                        {group}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {/* Table */}
              <Table
                columns={columns}
                dataSource={paginatedMembers}
                pagination={false}
                rowKey='id'
                scroll={{ y: 300 }}
                locale={{
                  emptyText: t('NoUsers'),
                }}
              />

              {/* Pagination */}
              <Row justify='center'>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredMembers?.length || 0}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size || 10);
                  }}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} users`
                  }
                />
              </Row>
            </Space>
          </div>

          {/* Form Container with slide animation */}
          {renderUserForm()}
        </div>

      </div>
    </Spin>
  );
};

export default memo(UserManagement);
